import type { ComponentSelections, DefaultProductPrices } from "./componentSelections";
import type { ProductGroupDto } from "../types/viewmodel";
import { ALWAYS_INCLUDED_SELECTION_TYPE } from "../constants/productSelection";

const CONFIG_URL_PARAM = "config";

const CHANGES_KEY = "changes";
const MULTIPLE_CHANGES_KEY = "multipleChanges";
const DESELECTIONS_KEY = "deselections";
const BUNDLE_ID_KEY = "bundleId";

const BASE64_STANDARD_PLUS = "+";
const BASE64_STANDARD_SLASH = "/";
const BASE64_URL_SAFE_PLUS = "-";
const BASE64_URL_SAFE_SLASH = "_";
const BASE64_PADDING = "=";

export interface CompactConfig {
	[CHANGES_KEY]?: { [productGroupId: string]: string };
	[MULTIPLE_CHANGES_KEY]?: { [productGroupId: string]: string[] };
	[DESELECTIONS_KEY]?: string[];
	[BUNDLE_ID_KEY]?: string;
}

export function buildDefaultPricesFromProductGroups(
	productGroups: ProductGroupDto[]
): DefaultProductPrices {
	const defaultPrices: DefaultProductPrices = {};

	for (const group of productGroups) {
		if (group.product?.id && group.selectionType !== ALWAYS_INCLUDED_SELECTION_TYPE) {
			defaultPrices[group.id] = {
				productId: group.product.id,
				price: { net: 0, gross: 0 }
			};
		}
	}

	return defaultPrices;
}

export function encodeConfigurationToUrl(
	selectedComponents: ComponentSelections,
	defaultProductPrices: DefaultProductPrices,
	bundleId: string
): string {
	const changes: { [productGroupId: string]: string } = {};
	const multipleChanges: { [productGroupId: string]: string[] } = {};
	const deselections: string[] = [];

	for (const [productGroupId, selection] of Object.entries(selectedComponents)) {
		if (Array.isArray(selection)) {
			// Always encode MULTIPLE entries, even [] (explicitly cleared), so the
			// recipient can distinguish "cleared" from "never touched".
			multipleChanges[productGroupId] = selection
				.map((s) => s.productId)
				.filter((id): id is string => id !== null);
			continue;
		}

		const defaultProductId = defaultProductPrices[productGroupId]?.productId;

		if (selection.productId === null) {
			if (defaultProductId) {
				deselections.push(productGroupId);
			}
			continue;
		}

		if (selection.productId !== defaultProductId) {
			changes[productGroupId] = selection.productId;
		}
	}

	const hasChanges = Object.keys(changes).length > 0;
	const hasMultipleChanges = Object.keys(multipleChanges).length > 0;
	const hasDeselections = deselections.length > 0;

	if (!hasChanges && !hasMultipleChanges && !hasDeselections) {
		return "";
	}

	const compactConfig: CompactConfig = { [BUNDLE_ID_KEY]: bundleId };
	if (hasChanges) {
		compactConfig[CHANGES_KEY] = changes;
	}
	if (hasMultipleChanges) {
		compactConfig[MULTIPLE_CHANGES_KEY] = multipleChanges;
	}
	if (hasDeselections) {
		compactConfig[DESELECTIONS_KEY] = deselections;
	}

	try {
		const jsonString = JSON.stringify(compactConfig);
		const base64 = btoa(jsonString)
			.replace(/\+/g, BASE64_URL_SAFE_PLUS)
			.replace(/\//g, BASE64_URL_SAFE_SLASH)
			.replace(/=+$/, "");
		return base64;
	} catch (error) {
		console.error("Failed to encode configuration:", error);
		return "";
	}
}

export function decodeConfigurationFromUrl(urlParam: string): CompactConfig | null {
	if (!urlParam || urlParam.trim() === "") {
		return null;
	}

	try {
		const urlSafeBase64 = urlParam
			.replace(/-/g, BASE64_STANDARD_PLUS)
			.replace(/_/g, BASE64_STANDARD_SLASH);
		const padded = urlSafeBase64.padEnd(
			urlSafeBase64.length + (4 - (urlSafeBase64.length % 4)) % 4,
			BASE64_PADDING
		);
		const jsonString = atob(padded);
		const parsed = JSON.parse(jsonString);

		if (!isValidCompactConfig(parsed)) {
			console.error("Invalid configuration format in URL");
			return null;
		}

		return parsed;
	} catch (error) {
		console.error("Failed to decode configuration from URL:", error);
		return null;
	}
}

function isNonEmptyString(value: unknown): value is string {
	return typeof value === "string" && value.trim() !== "";
}

function isStringRecord(value: unknown): value is Record<string, string> {
	if (typeof value !== "object" || value === null || Array.isArray(value)) {
		return false;
	}
	return Object.entries(value).every(
		([key, val]) => isNonEmptyString(key) && isNonEmptyString(val)
	);
}

function isStringArrayRecord(value: unknown): value is Record<string, string[]> {
	if (typeof value !== "object" || value === null || Array.isArray(value)) {
		return false;
	}
	return Object.entries(value).every(
		([key, val]) => isNonEmptyString(key) && isStringArray(val)
	);
}

function isStringArray(value: unknown): value is string[] {
	return Array.isArray(value) && value.every(isNonEmptyString);
}

export function isValidCompactConfig(config: unknown): boolean {
	if (!config || typeof config !== "object" || Array.isArray(config)) {
		return false;
	}

	const obj = config as Record<string, unknown>;
	const changes = obj[CHANGES_KEY];
	const multipleChanges = obj[MULTIPLE_CHANGES_KEY];
	const deselections = obj[DESELECTIONS_KEY];
	const bundleId = obj[BUNDLE_ID_KEY];

	if (changes !== undefined && !isStringRecord(changes)) {
		return false;
	}

	if (multipleChanges !== undefined && !isStringArrayRecord(multipleChanges)) {
		return false;
	}

	if (deselections !== undefined && !isStringArray(deselections)) {
		return false;
	}

	if (bundleId !== undefined && !isNonEmptyString(bundleId)) {
		return false;
	}

	return true;
}

export function buildShareUrl(
	baseUrl: string,
	configParam: string
): string {
	if (!configParam) {
		return baseUrl;
	}

	const url = new URL(baseUrl, window.location.origin);
	url.searchParams.set(CONFIG_URL_PARAM, configParam);
	return url.toString();
}

export function getConfigParamFromUrl(): string | null {
	const params = new URLSearchParams(window.location.search);
	return params.get(CONFIG_URL_PARAM);
}

export interface ShareableConfiguration {
	encoded: string;
	shareUrl: string;
}

export function buildAndEncodeConfiguration(
	selectedComponents: ComponentSelections,
	productGroups: ProductGroupDto[],
	bundleId: string
): ShareableConfiguration | null {
	const defaultPrices = buildDefaultPricesFromProductGroups(productGroups);
	const encoded = encodeConfigurationToUrl(selectedComponents, defaultPrices, bundleId);

	if (!encoded) {
		return null;
	}

	const baseUrl = window.location.pathname;
	const shareUrl = buildShareUrl(baseUrl, encoded);

	return { encoded, shareUrl };
}